/**
 * The client's comments (DESIGN.md "Comments sit in parts"): RailTabs with
 * Comments only, the shared threads, and the composer, "Comment on any part of
 * the page…". On the desk it is the right rail; on a tablet a sheet from the
 * right and on a phone a sheet from the bottom, both 8px in with their pegs.
 */
import { useState } from "react";
import { ArrowUp, ChevronDown, MessageSquare, PanelRightClose, X } from "lucide-react";
import { EmptyState } from "@/app/routes/dashboard/components/ViewStates";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import RailTabs from "@/app/routes/dashboard/agent/rail/RailTabs";
import CommentThread, { Avatar } from "@/app/routes/dashboard/content-calendar/comments/CommentThread";
import CommentComposer from "@/app/routes/dashboard/content-calendar/comments/CommentComposer";
import type { CommentSource } from "@/app/routes/dashboard/content-calendar/comments/commentModel";
import type { PostViewport } from "@/app/routes/dashboard/content-calendar/components/StatusBanner";
import type { ClientCommentPart, ClientCommentsData } from "./useClientComments";
import { AGENCY } from "../clientReviewModel";
import "@/app/routes/dashboard/agent/rail/rail.css";
import "@/app/routes/dashboard/content-calendar/comments/comments.css";
import "./clientReview.css";

export const CLIENT_COMPOSER_ID = "co-client-composer";
export const CLIENT_PHONE_COMPOSER_ID = "co-client-phone-composer";

export interface ClientCommentsProps {
  data: ClientCommentsData;
  brandName: string;
  readerName: string;
  /** The part the composer writes on: the open tab. */
  part: CommentSource;
  /** Under the head; none once comments are closed. */
  subtitle: string | null;
  /** Why the composer is off right now, or null when it takes a comment. */
  disabledReason: string | null;
  /** The idea and the brief are approved: one muted line in the composer's place. */
  closedLine: string | null;
  /** The part the comment is on, from a hovered part or C ("the hook"). */
  anchor: { label: string; onClear: () => void } | null;
  /** That part as the API names it, sent with the comment. */
  anchorPart: ClientCommentPart | null;
  /** Read only (a guest who is not a reviewer): no composer at all. */
  readOnly: boolean;
  postKey: string;
  /** The selected thread: open in the rail, its marker lit on the page. */
  openId: string | null;
  onOpenThread: (threadId: string | null) => void;
  /** Scroll the page to the thread's part and light its marker. */
  onShowOnPage: (threadId: string) => void;
}

function CommentsBody({
  data,
  brandName,
  readerName,
  part,
  disabledReason,
  closedLine,
  anchor,
  anchorPart,
  readOnly,
  postKey,
  openId,
  onOpenThread,
  onShowOnPage,
  phone,
}: ClientCommentsProps & { phone: boolean }) {
  // Open threads first, then the done ones with their check.
  const shown = [...data.open, ...data.done];
  return (
    <div className="co-cmt-panel">
      {data.threads.length === 0 ? (
        <div className="co-cmt-empty">
          <EmptyState
            minHeight={220}
            icon={<MessageSquare size={20} strokeWidth={1.5} aria-hidden="true" />}
            title="No comments yet"
            body={readOnly || disabledReason || closedLine ? "Nothing has been said on this post yet." : "Comment on any part of the page. Mohtawa reads it straight away."}
          />
        </div>
      ) : (
        <ul className="co-cmt-list" aria-label="Comments">
          {shown.map((thread) => (
            <li key={thread.id} id={`co-client-thread-${thread.id}`}>
              <CommentThread
                thread={thread}
                clientName={brandName}
                now={data.now}
                audience="client"
                open={openId === thread.id}
                onSelect={() => onOpenThread(openId === thread.id ? null : thread.id)}
                onRetry={thread.comment.sendState === "failed" ? () => data.retry(thread.id) : undefined}
                onReply={!readOnly && data.canReply(thread) ? (body) => data.reply(thread.id, body) : undefined}
                onToggleDone={readOnly || thread.id.startsWith("local:") ? undefined : () => data.toggleDone(thread.id)}
                onShowOnPage={() => onShowOnPage(thread.id)}
                phone={phone}
              />
            </li>
          ))}
        </ul>
      )}
      {readOnly ? null : (
        <CommentComposer
          key={postKey}
          source={part}
          audience="client"
          authorName={readerName}
          clientName={brandName}
          placeholder={phone ? "Comment on the whole post…" : "Comment on any part of the page…"}
          keysHint={phone ? undefined : "Enter posts · shift+enter new line · c to comment"}
          inputId={CLIENT_COMPOSER_ID}
          disabledReason={disabledReason}
          closedLine={closedLine}
          part={anchor}
          phone={phone}
          onPost={(body) => data.post(part, body, anchorPart)}
        />
      )}
    </div>
  );
}

/** The desk's right rail. */
export function ClientCommentsRail(props: ClientCommentsProps & { onCollapse: () => void }) {
  const { onCollapse, ...rest } = props;
  return (
    <aside aria-label="Comments" className="flex min-h-0 w-[360px] shrink-0 flex-col border-l border-co-line bg-co-rail">
      <RailTabs
        tab="comments"
        onTab={() => undefined}
        count={props.data.count}
        subtitle={props.subtitle}
        showChat={false}
        idPrefix="co-client-rail"
        collapse={
          <button type="button" className="co-railtabs-collapse" aria-label="Fold the comments" title="Fold" onClick={onCollapse}>
            <PanelRightClose aria-hidden strokeWidth={1.75} />
          </button>
        }
      />
      <div id="co-client-rail-panel-comments" role="tabpanel" aria-labelledby="co-client-rail-tab-comments" className="flex min-h-0 flex-1 flex-col">
        <CommentsBody {...rest} phone={false} />
      </div>
    </aside>
  );
}

/** The desk's folded rail: a narrow tab with Comments and its count. */
export function ClientCommentsTab({ count, onOpen }: { count: number; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={count > 0 ? `Open the comments, ${count}` : "Open the comments"}
      className="flex w-11 shrink-0 flex-col items-center gap-3 border-l border-co-line bg-co-rail pt-5 text-co-text2 outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent"
    >
      <MessageSquare className="size-4" strokeWidth={1.8} aria-hidden="true" />
      <span className="font-section text-[17px] font-semibold [writing-mode:vertical-rl]">
        Comments{count > 0 ? <span className="ml-1.5 font-body text-[12.5px] font-medium text-co-muted">{count}</span> : null}
      </span>
    </button>
  );
}

/** Tablet: a sheet from the right. Phone: a sheet from the bottom, tap the grabber for full height. */
export function ClientCommentsSheet(
  props: ClientCommentsProps & { viewport: PostViewport; isOpen: boolean; onOpenChange: (open: boolean) => void },
) {
  const { viewport, isOpen, onOpenChange, ...rest } = props;
  const phone = viewport === "phone";
  const [full, setFull] = useState(false);
  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      aria-label="Comments"
      hideCloseButton
      overlayClassName={phone ? "co-cmt-sheet-overlay--phone" : "co-cmt-sheet-overlay--tablet"}
      modalClassName={phone ? `co-cmt-sheet--phone${full ? " is-full" : ""}` : "co-cmt-sheet--tablet"}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {phone ? (
          <button
            type="button"
            className="co-cmt-sheet-grabber co-cr-grabber"
            aria-label={full ? "Half height" : "Full height"}
            onClick={() => setFull((value) => !value)}
          />
        ) : null}
        <RailTabs
          tab="comments"
          onTab={() => undefined}
          count={props.data.count}
          subtitle={props.subtitle}
          showChat={false}
          idPrefix="co-client-sheet"
          collapse={
            <button type="button" className="co-railtabs-collapse" aria-label="Close comments" title="Close" onClick={() => onOpenChange(false)}>
              {phone ? <ChevronDown aria-hidden strokeWidth={1.75} /> : <PanelRightClose aria-hidden strokeWidth={1.75} />}
            </button>
          }
        />
        <div id="co-client-sheet-panel-comments" role="tabpanel" aria-labelledby="co-client-sheet-tab-comments" className="flex min-h-0 flex-1 flex-col">
          <CommentsBody {...rest} phone={phone} />
        </div>
      </div>
    </KitModal>
  );
}

/**
 * Calendar / Comment composer · Phone (Pen XlAvc): tapping a part of the page
 * opens this floating glass sheet on the keyboard, "Commenting on" the part,
 * with a field that grows and a 44px post button. Posts at once.
 */
export function ClientPhoneComposer({
  isOpen,
  onClose,
  partLabel,
  number,
  readerName,
  disabledReason,
  onPost,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** "Concept · the hook". */
  partLabel: string | null;
  /** The marker number the comment takes on the page. */
  number: number | null;
  readerName: string;
  disabledReason: string | null;
  onPost: (body: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const ready = !disabledReason && !!draft.trim();
  if (!isOpen) return null;
  const post = () => {
    const body = draft.trim();
    if (!body || disabledReason) return;
    onPost(body);
    setDraft("");
    onClose();
  };
  return (
    <div
      role="dialog"
      aria-label={partLabel ? `Commenting on ${partLabel}` : "Comment"}
      className="co-glass-chrome fixed inset-x-2 bottom-2 z-40 flex flex-col gap-2.5 rounded-panel px-3.5 pb-3 pt-3.5"
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10.5px] tracking-[0.12em] text-co-muted">Commenting on</span>
        {partLabel ? (
          <span className="flex min-w-0 items-center gap-1.5 rounded-tag bg-co-accent-dim px-2 py-[3px] text-[12px] text-co-text">
            {number ? <span className="font-bold text-co-accent">{number}</span> : null}
            <span className="truncate">{partLabel}</span>
          </span>
        ) : (
          <span className="text-[12px] text-co-text">The whole post</span>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="-my-2.5 -mr-2 grid size-11 shrink-0 place-items-center rounded-pill text-co-muted outline-none focus-visible:outline-2 focus-visible:outline-co-accent"
        >
          <X className="size-3.5" strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
      <div className="co-cr-phone-field co-glass-control flex items-end gap-2.5 rounded-card p-2.5">
        <Avatar name={readerName} />
        <textarea
          id={CLIENT_PHONE_COMPOSER_ID}
          autoFocus
          dir="auto"
          rows={1}
          value={draft}
          disabled={!!disabledReason}
          aria-label="Your comment"
          placeholder="Comment…"
          onChange={(event) => {
            setDraft(event.target.value);
            const el = event.target;
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 6 * 24)}px`;
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              post();
            }
          }}
          className="min-h-6 min-w-0 flex-1 resize-none self-center bg-transparent text-[16px] leading-[1.5] text-co-text outline-none placeholder:text-co-faint"
        />
        <button
          type="button"
          onClick={post}
          disabled={!ready}
          aria-label="Post comment"
          className="grid size-11 shrink-0 place-items-center rounded-pill bg-co-accent-fill text-co-on-accent outline-none transition-opacity disabled:bg-co-tonal disabled:text-co-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
        >
          <ArrowUp className="size-4" strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
      <p className="text-[12px] text-co-muted">{disabledReason ?? `Posts to ${AGENCY} at once.`}</p>
    </div>
  );
}
